import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import ShareModal from '../components/ShareModal';
import ThreeDViewer from '../components/ThreeDViewer';
import './SeasonalGalleryPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SeasonalGalleryPage = () => {
  const navigate = useNavigate();
  const { user, logout, sessionToken } = useAuth();
  const [seasonalAlbums, setSeasonalAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState('');
  const [show3DViewer, setShow3DViewer] = useState(false);
  const [selected3DImage, setSelected3DImage] = useState(null);

  useEffect(() => {
    loadSeasonalAlbums();
  }, []);

  const loadSeasonalAlbums = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/seasonal-albums?include_images=true`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });

      if (response.data.success) {
        // Map 'images' to 'generated_images' for backward compatibility
        const albums = response.data.seasonal_albums.map(album => ({
          ...album,
          generated_images: album.images || album.generated_images || []
        }));
        setSeasonalAlbums(albums);
      }
    } catch (error) {
      console.error('Error loading seasonal albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteAlbum = async (albumId) => {
    if (!window.confirm('Are you sure you want to delete this entire collection?')) {
      return;
    }

    try {
      await axios.delete(`${API}/seasonal-albums/${albumId}`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });

      // Refresh the list
      loadSeasonalAlbums();
      alert('Collection deleted successfully!');
    } catch (error) {
      console.error('Error deleting album:', error);
      alert('Failed to delete collection');
    }
  };

  const deleteImage = async (albumId, imageIndex) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await axios.delete(`${API}/seasonal-albums/${albumId}/images/${imageIndex}`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });

      // Refresh the list first
      await loadSeasonalAlbums();
      
      // Then close modal and show success
      if (showDetailsModal && selectedAlbum?.id === albumId) {
        setShowDetailsModal(false);
        setSelectedAlbum(null);
      }
      
      alert('Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image');
    }
  };

  const handleViewDetails = (album) => {
    setSelectedAlbum(album);
    setShowDetailsModal(true);
  };

  const handleShare = (imageUrl) => {
    setShareImageUrl(imageUrl);
    setShowShareModal(true);
  };

  const handle3DView = (image) => {
    setSelected3DImage(image);
    setShow3DViewer(true);
  };

  const downloadImage = (imageData, fileName) => {
    const link = document.createElement('a');
    link.href = imageData.startsWith('data:') ? imageData : `data:image/jpeg;base64,${imageData}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div className="seasonal-gallery-page">
        <div className="gallery-container">
        <div className="gallery-header">
          <h1>🌟 My Seasonal Collections</h1>
          <p>View all your AI-generated seasonal try-on collections</p>
        </div>

        {loading ? (
          <div className="loading">Loading collections...</div>
        ) : seasonalAlbums.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎨</div>
            <h3>No Seasonal Collections Yet</h3>
            <p>Go to the Try-On page and generate your first seasonal collection - it&apos;s FREE!</p>
            <button className="create-btn" onClick={() => navigate('/tryon')}>
              Explore Seasonal Templates
            </button>
          </div>
        ) : (
          <div className="collections-grid">
            {seasonalAlbums.map((album) => (
              <div key={album.id} className="collection-card">
                <div className="collection-header">
                  <h3>{album.template_name}</h3>
                  <span className="collection-date">
                    {new Date(album.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="collection-preview">
                  <div className="preview-grid">
                    {(album.generated_images || []).slice(0, 4).map((img, idx) => (
                      <div key={idx} className="preview-image">
                        <img
                          src={img.result_image?.startsWith('data:') ? img.result_image : `data:image/jpeg;base64,${img.result_image}`}
                          alt={img.garment_name || 'Garment'}
                        />
                        <div className="image-overlay">
                          <span className="garment-name">{img.garment_name || 'Item'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="collection-info">
                  <span className="image-count">
                    📷 {album.generated_images.length} images
                  </span>
                  <span className="credits-badge">
                    {album.credits_used === 0 ? '🎉 FREE' : `💎 ${album.credits_used} credits`}
                  </span>
                </div>

                <div className="collection-actions">
                  <button
                    className="btn-primary"
                    onClick={() => handleViewDetails(album)}
                  >
                    View Details
                  </button>
                  <button
                    className="delete-collection-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteAlbum(album.id);
                    }}
                  >
                    🗑️ Delete Collection
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>

        {showDetailsModal && selectedAlbum && (
          <DetailsModal
            album={selectedAlbum}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedAlbum(null);
            }}
            onDownload={downloadImage}
            onDeleteImage={deleteImage}
            onShare={handleShare}
            on3DView={handle3DView}
          />
        )}
        
        {showShareModal && (
          <ShareModal
            isOpen={showShareModal}
            imageUrl={shareImageUrl}
            onClose={() => setShowShareModal(false)}
          />
        )}
        
        {show3DViewer && selected3DImage && (
          <div className="modal-overlay" onClick={() => setShow3DViewer(false)}>
            <div className="modal-content threed-modal" onClick={(e) => e.stopPropagation()}>
              <button className="close-btn" onClick={() => setShow3DViewer(false)}>×</button>
              <ThreeDViewer 
                imageUrl={selected3DImage.result_image}
                productName={selected3DImage.garment_name}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const DetailsModal = ({ album, onClose, onDownload, onDeleteImage, onShare, on3DView }) => {
  return (
    <div className="modal-overlay-fullscreen" onClick={onClose}>
      <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header - Thumb-friendly top */}
        <div className="modal-sheet-header">
          <button className="modal-back-btn" onClick={onClose}>
            ← Back
          </button>
          <h2 className="modal-title">{album.template_name}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Quick Info Bar */}
        <div className="modal-info-bar">
          <span className="info-pill">{(album.generated_images || []).length} images</span>
          <span className="info-pill">
            {album.credits_used === 0 ? '✨ FREE' : `💎 ${album.credits_used} credits`}
          </span>
        </div>

        {/* Images Grid - Optimized for mobile */}
        <div className="modal-images-grid">
          {(album.generated_images || []).map((img, idx) => (
            <div key={idx} className="modal-image-card">
              <div className="modal-image-wrapper">
                <img
                  src={img.result_image?.startsWith('data:') ? img.result_image : `data:image/jpeg;base64,${img.result_image}`}
                  alt={img.garment_name || 'Garment'}
                  loading="lazy"
                />
              </div>
              
              <div className="modal-product-info">
                <h3 className="product-name">{img.garment_name}</h3>
                <p className="product-brand">{img.garment_brand}</p>
                
                {img.product_link && (
                  <a 
                    href={img.product_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="product-buy-btn"
                  >
                    <span role="img" aria-label="cart">🛒</span> Shop Now
                  </a>
                )}
              </div>
              
              {/* Action Buttons - Thumb-friendly bottom placement */}
              <div className="modal-action-row">
                <button className="action-icon-btn" onClick={() => onDownload(img.result_image, `${album.template_name}-${idx + 1}.jpg`)}>
                  <span role="img" aria-label="download">⬇️</span>
                </button>
                <button className="action-icon-btn" onClick={() => onShare(img.result_image)}>
                  <span role="img" aria-label="share">📤</span>
                </button>
                <button className="action-icon-btn" onClick={() => on3DView(img)}>
                  <span role="img" aria-label="3d view">🔄</span>
                </button>
                <button 
                  className="action-icon-btn danger" 
                  onClick={() => {
                    onClose();
                    onDeleteImage(album.id, idx);
                  }}
                >
                  <span role="img" aria-label="delete">🗑️</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeasonalGalleryPage;
